/* eslint-disable import/no-extraneous-dependencies */
import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Divider,
  IconButton,
  useTheme,
  alpha,
  Grid
} from '@mui/material';
import {
  Download as DownloadIcon,
  Description as DocumentIcon,
  ArrowBack
} from '@mui/icons-material';
import { httpRequest } from 'src/services/httpRequest';
import toast from 'react-hot-toast';

interface Document {
  BidDocsID: number;
  BidID: number;
  DocumentName: string;
  Active: boolean;
  DocumentType: string;
  DocumentDescription: string;
}

interface Category {
  CategoryID: number;
  CategoryName: string;
}

interface ChildDocument {
  ChildBidDocsID: number;
  DocumentName: string;
  DocumentType: string;
  DocumentDescription: string;
  InsertDate: string;
  DocumentUrl: string;
}

interface HomeBid {
  bidId: string;
  Type: string;
  Title: string;
  category: string;
  AdDate: string;
  OpeningDate: string;
  status: string;
  awardedTo?: string;
  uploadedFiles: number;
  departmentId: string;
  contractNo: string;
  ChildDescription?: string;
  documents?: {
    id: number;
    name: string;
    type: string;
    description?: string;
  }[];
  ChildBidDocuments?: ChildDocument[];
}

interface ApiBid {
  BidID: number;
  TypeID: number;
  contractNo: string;
  Title: string;
  CategoryIDs: number[];
  DepartmentID: number;
  GoLiveDate: string | null;
  BidDeadLineDate: string;
  AdDate: string;
  OpeningDate: string;
  AwardDate: string | null;
  StatusID: number;
  ChildDescription: string | null;
  AwardedTo: string | null;
  AwardedToVendor?: string;
  AwardedToContact?: string;
  Status: string;
  Type: string;
  Categories: Category[];
  DocumentFileUpload: Document[];
  PONumber?: string;
}

interface BidDetailsProps {
  bidData: HomeBid | Partial<ApiBid>;
  onBack: () => void;
}

type DownloadKind = 'parent' | 'child' | 'fromChild';
const makeDownloadingKey = (kind: DownloadKind, id: number) => `${kind}:${id}`;

const ChildBidDetails: React.FC<BidDetailsProps> = ({ bidData, onBack }) => {
  const REACT_APP_ENV = process.env.REACT_APP_ENV;
  const theme = useTheme();
  const [downloadingKey, setDownloadingKey] = React.useState<string | null>(
    null
  );

  const [downloadingAll, setDownloadingAll] =
    React.useState<DownloadKind | null>(null);

  function getBaseUrl(url) {
    const parsedUrl = new URL(url);
    return `${parsedUrl.protocol}//${parsedUrl.hostname}${
      parsedUrl.port ? `:${parsedUrl.port}` : ''
    }`;
  }

  /** Single download */
  const handleDownloadDocument = async (id: number, kind: DownloadKind) => {
    const key = makeDownloadingKey(kind, id);
    try {
      setDownloadingKey(key);

      const paramName = kind === 'child' ? 'ChildBidDocsID' : 'DocumentID';

      // Call your API to get the blob URL
      const response = await httpRequest(
        'GET',
        `/bid_portal/bids/download?${paramName}=${id}`
      );

      const fileUrl = (response as any)?.data?.documentUrl;
      if (!fileUrl) {
        toast.error('File URL not found.');
        return;
      }

      // ✅ Just open it directly — browser will handle download
      window.open(fileUrl, '_blank');
      toast.success('Download started.');
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Error downloading document.');
    } finally {
      setDownloadingKey(null);
    }
  };

  /** Download all (parent or child) */
  const handleDownloadAllDocuments = async (
    bidId: string,
    kind: DownloadKind
  ) => {
    try {
      setDownloadingAll(kind);
      let paramName = '';

      if (kind === 'fromChild') {
        paramName = 'ChildBidID';
      } else if (kind === 'child') {
        paramName = 'ParentBidID';
      } else {
        paramName = 'BidID'; // Default case
      }

      const baseUrl = getBaseUrl(window.location.href);
      const baseLocalUrl = 'http://localhost:5000';
      const apiUrl = REACT_APP_ENV === 'development' ? baseLocalUrl : baseUrl;
      const url = `${apiUrl}/api/bid_portal/bids/downloadDocumentAsZip?${paramName}=${bidId}`;
      const link = document.createElement('a');
      link.href = url;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`All ${kind} documents downloaded successfully`);
    } catch (err) {
      console.error(err);
      toast.error(`Error downloading ${kind} documents.`);
    } finally {
      setDownloadingAll(null);
    }
  };
  // const handleDownloadAllDocuments = async (
  //   bidId: string,
  //   kind: DownloadKind
  // ) => {
  //   try {
  //     setDownloadingAll(kind);
  //     let paramName = '';

  //     if (kind === 'fromChild') {
  //       paramName = "ChildBidID";
  //     } else if (kind === 'child') {
  //       paramName = "ParentBidID";
  //     } else {
  //       paramName = "BidID"; // Default case
  //     }
  //     const response = await httpRequest(
  //       'GET',
  //       `/bid_portal/bids/download?${paramName}=${bidId}`,
  //       null,
  //       { responseType: 'blob' }
  //     );

  //     if (response) {
  //       // const blobPart = (response as any).data ?? response;
  //       // const url = window.URL.createObjectURL(new Blob([blobPart]));
  //       const url = `${window.location.href}/api/bid_portal/bids/downloadDocumentAsZip?${paramName}=${bidId}`;
  //       const link = document.createElement('a');
  //       link.href = url;
  //       const contentDisposition =
  //         (response as any)?.headers?.get?.('content-disposition') ??
  //         (response as any)?.headers?.['content-disposition'];
  //       const fileName = contentDisposition
  //         ? contentDisposition.split('filename=')[1]?.replace(/"/g, '')
  //         : `${kind}-bid-${bidId}-documents.zip`;
  //       link.setAttribute(
  //         'download',
  //         fileName || `${kind}-bid-${bidId}-documents.zip`
  //       );
  //       document.body.appendChild(link);
  //       link.click();
  //       link.remove();
  //       window.URL.revokeObjectURL(url);
  //       toast.success(`All ${kind} documents downloaded successfully`);
  //     } else {
  //       toast.error(`Failed to download ${kind} documents`);
  //     }
  //   } catch (err) {
  //     console.error(err);
  //     toast.error(`Error downloading ${kind} documents.`);
  //   } finally {
  //     setDownloadingAll(null);
  //   }
  // };

  const isHomeFormat = 'bidId' in bidData;

  const normalizedBid = {
    bidId: isHomeFormat ? bidData.bidId : String(bidData.BidID),
    Title: bidData.Title,
    ChildDescription: bidData.ChildDescription,
    contractNo: bidData.contractNo,
    adDate: isHomeFormat ? bidData.AdDate : bidData.AdDate,
    openingDate: isHomeFormat ? bidData.OpeningDate : bidData.OpeningDate,
    status: isHomeFormat ? bidData.status : bidData.Status,
    awardedTo: isHomeFormat ? bidData.awardedTo : bidData.AwardedTo,
    awardedToVendor:
      'AwardedToVendor' in bidData ? bidData.AwardedToVendor : undefined,
    awardedToContact:
      'AwardedToContact' in bidData ? bidData.AwardedToContact : undefined,
    Type: isHomeFormat ? bidData.Type : bidData.Type,
    poNumber: 'PONumber' in bidData ? bidData.PONumber : undefined
  };

  const childDocuments = (
    isHomeFormat
      ? bidData?.ChildBidDocuments?.map((doc) => ({
          ChildBidDocsID: doc.ChildBidDocsID,
          BidID: parseInt(bidData.bidId),
          DocumentName: doc.DocumentName,
          Active: true,
          DocumentType: doc.DocumentType,
          DocumentDescription: doc.DocumentDescription || ''
        })) || []
      : []
  ) as Array<
    Pick<
      Document,
      'BidID' | 'DocumentName' | 'DocumentType' | 'DocumentDescription'
    > & {
      ChildBidDocsID: number;
    }
  >;

  const formatDownloadDate = (dateString: string) => {
    if (!dateString) return '-';

    try {
      const date = new Date(dateString);
      return new Intl.DateTimeFormat('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      }).format(date);
    } catch (e) {
      return '-';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
        <IconButton
          onClick={onBack}
          sx={{
            bgcolor: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.main,
            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.2) }
          }}
        >
          <ArrowBack />
        </IconButton>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            color: theme.palette.primary.main,
            position: 'relative',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 100,
              height: 4,
              borderRadius: 2,
              backgroundColor: theme.palette.primary.main
            }
          }}
        >
          Back to Bids
        </Typography>
      </Box>

      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2
          }}
        >
          <Typography variant="h4">Bid Details</Typography>
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', mb: 2 }}>
              <Typography
                variant="subtitle1"
                component="span"
                sx={{ fontWeight: 'bold', mr: 1 }}
              >
                Contract No:
              </Typography>
              <Typography variant="body1">
                {normalizedBid.contractNo}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', mb: 2 }}>
              <Typography
                variant="subtitle1"
                component="span"
                sx={{ fontWeight: 'bold', mr: 1 }}
              >
                Title:
              </Typography>
              <Typography variant="body1">{normalizedBid.Title}</Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', mb: 2 }}>
              <Typography
                variant="subtitle1"
                component="span"
                sx={{ fontWeight: 'bold', mr: 1 }}
              >
                Description:
              </Typography>
              <Typography variant="body1">
                {normalizedBid.ChildDescription || 'No description available'}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', mb: 2 }}>
              <Typography
                variant="subtitle1"
                component="span"
                sx={{ fontWeight: 'bold', mr: 1 }}
              >
                Ad Date:
              </Typography>
              <Typography variant="body1">
                {normalizedBid.adDate
                  ? new Date(normalizedBid.adDate).toLocaleDateString('en-US')
                  : ''}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', mb: 2 }}>
              <Typography
                variant="subtitle1"
                component="span"
                sx={{ fontWeight: 'bold', mr: 1 }}
              >
                Opening Date:
              </Typography>
              <Typography variant="body1">
                {normalizedBid.openingDate
                  ? formatDownloadDate(normalizedBid.openingDate)
                  : ''}
              </Typography>
            </Box>
          </Grid>

          {normalizedBid.status?.toLowerCase() === 'awarded' && (
            <>
              <Grid item xs={12} md={6}>
                <Box sx={{ display: 'flex', mb: 2 }}>
                  <Typography
                    variant="subtitle1"
                    component="span"
                    sx={{ fontWeight: 'bold', mr: 1, color: 'primary.main' }}
                  >
                    Awarded To:
                  </Typography>
                  <Typography variant="body1">
                    {normalizedBid.awardedToVendor ||
                      'Vendor information not available'}
                  </Typography>
                  {normalizedBid.awardedToContact && (
                    <Typography variant="body2" color="text.secondary">
                      {normalizedBid.awardedToContact}
                    </Typography>
                  )}
                </Box>
              </Grid>

              {normalizedBid.poNumber && (
                <>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: 'flex', mb: 2 }}>
                      <Typography
                        variant="subtitle1"
                        component="span"
                        sx={{ fontWeight: 'bold', mr: 1 }}
                      >
                        PO Number:
                      </Typography>
                      <Typography variant="body1">
                        {normalizedBid.poNumber}
                      </Typography>
                    </Box>
                  </Grid>
                </>
              )}
            </>
          )}
        </Grid>

        {/* Child docs */}
        {childDocuments?.length > 0 && (
          <>
            <Divider sx={{ my: 4 }} />
            <Typography variant="h6" gutterBottom>
              Child Bid Documents
            </Typography>
            <Box sx={{ mt: 2 }}>
              {childDocuments?.map((doc) => {
                const key = makeDownloadingKey('child', doc.ChildBidDocsID);
                const isDownloading = downloadingKey === key;
                return (
                  <Paper
                    key={doc?.ChildBidDocsID}
                    sx={{
                      p: 2,
                      mb: 2,
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' }, // Stack vertically on small screens
                      alignItems: 'center', // Center the items on smaller screens
                      overflow: 'hidden' // Prevent overflow outside the Paper
                    }}
                  >
                    <DocumentIcon
                      sx={{
                        mr: 2,
                        mt: { xs: 1, sm: 0.5 }, // Adjust the margin-top for small screens
                        color: 'primary.main'
                      }}
                    />
                    <Box
                      sx={{
                        flexGrow: 1,
                        textAlign: { xs: 'center', sm: 'left' },
                        overflow: 'hidden'
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 'bold',
                          whiteSpace: 'normal', // Allow text to wrap if needed
                          overflow: 'hidden', // Hide overflowed content
                          textOverflow: 'ellipsis', // Add ellipsis for overflowed text
                          wordBreak: 'break-word', // Allow long words to break and wrap
                          width: '100%', // Ensure the Box doesn't stretch beyond its container
                          textAlign: { xs: 'center', sm: 'left' } // Keep alignment responsive
                        }}
                      >
                        {doc?.DocumentName}
                      </Typography>
                      {doc?.DocumentDescription && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            mt: 1,
                            fontSize: { xs: '0.85rem', sm: '1rem' },
                            textAlign: { xs: 'center', sm: 'left' }
                          }}
                        >
                          {doc?.DocumentDescription}
                        </Typography>
                      )}
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={isDownloading ? null : <DownloadIcon />}
                      onClick={() =>
                        handleDownloadDocument(doc?.ChildBidDocsID, 'child')
                      }
                      disabled={!!downloadingKey}
                      sx={{
                        mt: { xs: 2, sm: 0 },
                        minWidth: '120px'
                      }}
                    >
                      {isDownloading ? 'Downloading...' : 'Download'}
                    </Button>
                  </Paper>
                );
              })}
            </Box>
            {childDocuments?.length > 1 && (
              <Box sx={{ mt: 3, textAlign: 'center' }}>
                <Button
                  variant="contained"
                  startIcon={
                    downloadingAll === 'child' ? null : <DownloadIcon />
                  }
                  onClick={() =>
                    handleDownloadAllDocuments(normalizedBid.bidId, 'fromChild')
                  }
                  disabled={!!downloadingKey || !!downloadingAll}
                >
                  {downloadingAll === 'child'
                    ? 'Downloading...'
                    : 'Download All Child Bid Files (ZIP)'}
                </Button>
              </Box>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
};

export default ChildBidDetails;
